import React from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { ModalPortal } from "../../../components/ModalPortal/ModalPortal";
import { formatDate, RUNS_CAL_WEEK_SHORT } from "../requestDetailUtils";

export interface RequestDetailRunsCalendarModalProps {
  runsCalYm: { y: number; m: number };
  runsCalTitle: string;
  runsCalCells: (number | null)[];
  runsCalDaysWithRuns: Set<number>;
  runsCalSelectedDay: number | null;
  runsCalList: string[];
  runsCalToday: Date;
  setRunsCalSelectedDay: React.Dispatch<React.SetStateAction<number | null>>;
  handleCloseRunsCalendar: () => void;
  shiftRunsCalMonth: (delta: number) => void;
}

export const RequestDetailRunsCalendarModal: React.FC<RequestDetailRunsCalendarModalProps> = ({
  runsCalYm,
  runsCalTitle,
  runsCalCells,
  runsCalDaysWithRuns,
  runsCalSelectedDay,
  runsCalList,
  runsCalToday,
  setRunsCalSelectedDay,
  handleCloseRunsCalendar,
  shiftRunsCalMonth,
}) => (
  <ModalPortal>
    <div className="detail-schedule-modal-overlay" onClick={handleCloseRunsCalendar}>
      <div
        className="detail-schedule-modal detail-runs-cal-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="detail-schedule-modal-head">
          <h3>Запуски проверок</h3>
          <button
            type="button"
            className="detail-tasklist-close"
            onClick={handleCloseRunsCalendar}
            aria-label="Закрыть календарь"
          >
            <X size={18} />
          </button>
        </div>
        <div className="detail-schedule-modal-body">
          <div className="detail-runs-cal-nav">
            <button
              type="button"
              className="detail-runs-cal-nav-btn"
              onClick={() => shiftRunsCalMonth(-1)}
              aria-label="Предыдущий месяц"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="detail-runs-cal-title">{runsCalTitle}</span>
            <button
              type="button"
              className="detail-runs-cal-nav-btn"
              onClick={() => shiftRunsCalMonth(1)}
              aria-label="Следующий месяц"
            >
              <ChevronRight size={20} />
            </button>
          </div>
          <div className="detail-runs-cal-weekdays">
            {RUNS_CAL_WEEK_SHORT.map((w) => (
              <span key={w} className="detail-runs-cal-weekday">
                {w}
              </span>
            ))}
          </div>
          <div className="detail-runs-cal-grid">
            {runsCalCells.map((day, idx) => {
              if (day === null) {
                return (
                  <div
                    key={`empty-${runsCalYm.y}-${runsCalYm.m}-${idx}`}
                    className="detail-runs-cal-cell detail-runs-cal-cell--empty"
                    aria-hidden
                  />
                );
              }
              const hasRuns = runsCalDaysWithRuns.has(day);
              const isSelected = runsCalSelectedDay === day;
              const isToday =
                runsCalToday.getFullYear() === runsCalYm.y &&
                runsCalToday.getMonth() === runsCalYm.m &&
                runsCalToday.getDate() === day;
              return (
                <button
                  key={`${runsCalYm.y}-${runsCalYm.m}-${day}`}
                  type="button"
                  className={[
                    "detail-runs-cal-cell",
                    hasRuns ? "detail-runs-cal-cell--has-runs" : "",
                    isSelected ? "detail-runs-cal-cell--selected" : "",
                    isToday ? "detail-runs-cal-cell--today" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-label={
                    hasRuns
                      ? `${day}, есть запуск проверки`
                      : `${day}`
                  }
                  onClick={() => {
                    if (!hasRuns) {
                      setRunsCalSelectedDay(null);
                      return;
                    }
                    setRunsCalSelectedDay((prev) => (prev === day ? null : day));
                  }}
                >
                  <span className="detail-runs-cal-cell-day">{day}</span>
                  {hasRuns ? <span className="detail-runs-cal-cell-dot" aria-hidden /> : null}
                </button>
              );
            })}
          </div>
          <p className="detail-runs-cal-legend">
            <span className="detail-runs-cal-legend-dot" aria-hidden />
            Дни с запуском проверки
          </p>
          <div className="detail-runs-cal-list">
            {runsCalList.length === 0 ? (
              <span className="detail-runs-cal-list-empty">Нет запусков в этом месяце</span>
            ) : (
              runsCalList.map((iso, i) => (
                <div key={`${iso}-${i}`} className="detail-runs-cal-list-item">
                  {formatDate(iso)}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  </ModalPortal>
);
